import Seo from "../components/Seo";
import { MarketingHeader, CTASection, FAQBlock } from "../components/MarketingShell";
import LegalFooter from "../components/LegalFooter";
import { Link } from "react-router-dom";

const EXAMPLES = [
  { hi: "अरे यार, ये तो हद हो गई!", lit: "Oh friend, this has become the limit!", ours: "Damn, this is beyond ridiculous!" },
  { hi: "भाई, चिंता मत कर, सब ठीक हो जाएगा।", lit: "Brother, don't do worry, everything will do fine.", ours: "Bro, chill — it'll all work out." },
  { hi: "क्या बात है, आप तो छा गए!", lit: "What talk is, you have covered!", ours: "Wow, you absolutely crushed it!" },
  { hi: "सुन ना, एक बात बताऊं?", lit: "Listen not, one thing tell?", ours: "Hey, can I tell you something?" },
  { hi: "पगला गया है क्या तू?", lit: "Has become mad what you?", ours: "Have you completely lost it?" },
  { hi: "थोड़ा सब्र रख, यार।", lit: "Some patience keep, friend.", ours: "Just have a little patience, man." },
  { hi: "बच्चों जैसी बातें मत कर।", lit: "Children like talks don't do.", ours: "Stop being so childish." },
  { hi: "अच्छा सुन, फिर मिलते हैं।", lit: "Good listen, then meet.", ours: "Alright, catch you later." },
];

const USE_CASES = [
  { role: "YouTubers in Hindi", body: "Add English subtitles to unlock the massive non-Hindi-speaking audience on YouTube. Videos with captions get ~15% more watch time and 40% more international impressions." },
  { role: "Bollywood clip creators", body: "Reels, dialogue compilations, meme edits — LinguaSub keeps the sarcasm, punchlines and character voice intact instead of flattening them into robotic English." },
  { role: "Indian educators", body: "Coaching classes, EdTech creators, professors — reach students who prefer English or don't speak Hindi natively without re-recording anything." },
  { role: "Wedding videographers", body: "Send NRI relatives a subtitled version of family video messages, speeches, and events." },
  { role: "Journalists & documentarians", body: "Interview-heavy footage in Hindi can be exported with clean English subtitles for international festival submissions." },
  { role: "Corporate India", body: "Internal town halls, leadership videos, or training modules recorded in Hindi can be pushed to global teams in minutes." },
];

const FAQ = [
  { q: "How is this different from Google Translate on YouTube?", a: "YouTube's auto-translate is literal, word-for-word, and often robotic — 'ye toh kamaal hai' becomes 'This is wonderful' instead of the natural 'Damn, that's amazing.' LinguaSub uses GPT-5.4 with a subtitle-specific prompt that preserves tone, sarcasm, honorifics, and Hinglish." },
  { q: "Does it handle Hinglish (mixed Hindi + English)?", a: "Yes — Hinglish is one of the hardest cases for generic tools because they can't decide which language to treat it as. LinguaSub reads the whole transcript for context, then translates each segment into natural English while keeping proper nouns intact." },
  { q: "Can it transcribe Bhojpuri, Magahi, or Awadhi?", a: "Yes, though Whisper's models are strongest for standard Hindi. For regional dialects we send Whisper a dialect-aware prompt so it still captures the audio, then GPT-5.4 handles idiomatic phrasing in translation. Accuracy is highest on clean audio." },
  { q: "How fast is a 10-minute Hindi video?", a: "Roughly 45 seconds transcription + 20 seconds translation + 30 seconds burn-in ≈ under 2 minutes total. You'll be downloading the finished subtitled MP4 while your coffee is still hot." },
  { q: "Do I keep rights to the output?", a: "Yes. Any paid plan grants full commercial rights. Free-tier output is fine for personal, educational, or open-source use. See /terms." },
  { q: "What if the translation is wrong on one line?", a: "Open the edit panel, click that segment, retype the English, hit save. Re-render if you want the fix baked into the video. Takes 30 seconds." },
];

export default function HindiToEnglishPage() {
  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white">
      <Seo path="/hindi-to-english-subtitle-translator" title="Hindi to English subtitle translator" description="Turn Hindi videos into English subtitles that keep the humour, honorifics and Hinglish intact. Auto-transcribe with Whisper, translate with GPT-5.4, export SRT + subtitled MP4." />
      <MarketingHeader />

      <section className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">Hindi → English</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter leading-tight mb-6 max-w-4xl">
            Hindi to English subtitle translator that keeps the <span className="text-blue-500">soul</span>.
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed mb-8">
            Auto-transcribe Hindi audio, translate each line with a subtitle-trained GPT-5.4 model, edit against the video, and export a finished .srt or a burned-in MP4. The sarcasm, the yaar, the "arre bhai" — all intact.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/" className="h-12 px-6 bg-white text-zinc-950 hover:bg-zinc-200 font-semibold text-sm inline-flex items-center rounded-none">Upload a Hindi video →</Link>
            <Link to="/pricing" className="h-12 px-6 border border-zinc-700 hover:bg-zinc-900 text-white text-sm inline-flex items-center rounded-none">See pricing</Link>
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Real examples</div>
          <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-10 text-white">
            The literal translation vs the one that actually sounds like a person.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {EXAMPLES.map((ex) => (
              <div key={ex.hi} className="border border-zinc-800 bg-zinc-900/40 p-5">
                <div className="text-xs font-mono text-zinc-400 mb-2">{ex.hi}</div>
                <div className="text-xs text-zinc-600 line-through decoration-red-400/30 mb-1">Generic: {ex.lit}</div>
                <div className="text-sm text-white leading-relaxed">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mr-2">LinguaSub</span>{ex.ours}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-800 bg-zinc-900/40">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-10 text-white">
            Who uses Hindi → English subtitles.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {USE_CASES.map((u) => (
              <div key={u.role} className="border border-zinc-800 bg-zinc-950 p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">{u.role}</div>
                <p className="text-sm text-zinc-300 leading-relaxed">{u.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQBlock items={FAQ} />
      <CTASection title="Try it on your Hindi video." subtitle="One-time free trial for new users. Upload up to 5 GB." />
      <div className="max-w-6xl mx-auto px-4 md:px-8"><LegalFooter /></div>
    </div>
  );
}
